'use client';

import { memo as Memo, useRef, useEffect, useState, useCallback } from 'react';

//* HOC's
import withUIContext from '@context/consumerHOC/UIConsumer';

//* Components
import Page from '@components/global/Page';
import Faq from '@components/pages/Services/Faq';
import AwardsCont from '@components/global/AwardsCont';
import Partners from '@components/pages/AboutUs/Partners';
import WhatWeDo from '@components/pages/AboutUs/WhatWeDo';
import OurValues from '@components/pages/AboutUs/OurValues';
import WhyChoose from '@components/pages/Services/WhyChoose';
import HTMLContainer from '@components/global/HTMLContainer';
import TopSection from '@components/pages/AboutUs/TopSection';
import TextHoverAnimations from '@components/global/SvgComponents/TextHoverAnimations';

const AboutUs = Memo(({ pageData, winHeight, darkMode }) => {
	//! States
	const [secObj, setSecObj] = useState({
		sec1: false,
		sec2: false,
		sec3: false,
		sec4: false,
		sec5: false,
	});

	//! Refs
	const sec1 = useRef();
	const sec2 = useRef();
	const sec3 = useRef();
	const sec4 = useRef();
	const sec5 = useRef();

	const sectionAnimation = useCallback(() => {
		let arr = [];

		if (sec1.current) {
			arr = [
				sec1?.current?.getBoundingClientRect(),
				sec2?.current?.getBoundingClientRect(),
				sec3?.current?.getBoundingClientRect(),
				sec4?.current?.getBoundingClientRect(),
				sec5?.current?.getBoundingClientRect(),
			];

			let newObj = secObj;
			for (let i = 0; i < arr.length; i++) {
				newObj = { ...newObj, [`sec${i + 1}`]: arr[i].top < winHeight };
			}

			if (JSON.stringify(newObj) !== JSON.stringify(secObj)) {
				setSecObj(newObj);
			}
		}
	}, [sec1, sec2, sec3, sec4, sec5, secObj, winHeight]);

	useEffect(() => {
		sectionAnimation();
	}, [sec1.current, sec2.current, sec3.current, sec4.current, sec5.current]);

	useEffect(() => {
		window.addEventListener('scroll', sectionAnimation);

		return () => {
			window.removeEventListener('scroll', sectionAnimation);
		};
	}, [sec1, sec2, secObj]);

	return (
		<Page>
			<HTMLContainer>
				<div
					ref={sec1}
					className='section-item'>
					<TopSection
						active={secObj.sec1}
						{...{ ...pageData.hero, sub_article: { title: pageData.second.title, description: pageData.second.description } }}
					/>
				</div>
			</HTMLContainer>

			<HTMLContainer>
				<div
					ref={sec2}
					className='section-item'>
					<WhatWeDo
						active={secObj.sec2}
						{...pageData.what_we_do}
					/>
				</div>
			</HTMLContainer>

			{pageData.choose?.items?.length > 0 ? (
				<HTMLContainer>
					<div className='section-item'>
						<WhyChoose
							title={pageData.choose.title}
							items={pageData.choose.items}
						/>
					</div>
				</HTMLContainer>
			) : null}

			<div
				ref={sec3}
				className='crStick section-item'>
				<AwardsCont
					type={1}
					count={4}
					link={'/awards/'}
					active={secObj.sec3}
					data={pageData.awards}
				/>
			</div>

			<TextHoverAnimations />

			<HTMLContainer>
				<div
					ref={sec4}
					className='section-item'>
					{pageData.partners.items?.length > 0 ? (
						<Partners
							darkMode={darkMode}
							active={secObj.sec4}
							{...pageData.partners}
						/>
					) : null}
				</div>

				<div
					ref={sec5}
					className='section-item'>
					<OurValues
						active={secObj.sec5}
						{...pageData.our_values}
					/>
				</div>
			</HTMLContainer>

			{pageData.faqs?.length > 0 ? (
				<HTMLContainer>
					<div className='section-item'>
						<Faq faqs={pageData.faqs} />
					</div>
				</HTMLContainer>
			) : null}
		</Page>
	);
});

export default withUIContext(AboutUs, ['winHeight', 'darkMode']);
